import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS as S } from './constants';

// One entity per AccessWindow, as GET /accessWindows/open-access returns it (devices[] and people[] inline).
export const schedulesAdapter = createEntityAdapter({ selectId: (s) => s.accessWindowID });

const initialState = {
	// True between pageMounted and pageUnmounted: the saga session lives exactly that long.
	hydrated: false,
	query: { q: '' },
	schedules: schedulesAdapter.getInitialState({ status: S.IDLE, error: null }),
	// False on Inception orgs: no per-permission schedules, so the cards have no PEOPLE.
	supportsPermissionSchedules: true,
	// accessWindowID → { running, rows: [{ deviceID, name, success, retryable, reason, removed? }] }
	deviceSaves: {},
	editor: null
};

const openAccessSlice = createSlice({
	name: 'openAccess',
	initialState,
	reducers: {
		pageMounted: (state, action) => {
			state.hydrated = true;
			state.query.q = action.payload?.q ?? '';
		},
		pageUnmounted: (state) => {
			state.hydrated = false;
			state.editor = null;
		},
		queryChanged: (state, action) => {
			state.query.q = action.payload.q;
		},
		queryCleared: (state) => {
			state.query.q = '';
		},

		schedulesRequested: (state) => {
			state.schedules.status = S.LOADING;
			state.schedules.error = null;
		},
		schedulesReceived: (state, action) => {
			schedulesAdapter.setAll(state.schedules, action.payload.schedules ?? []);
			state.supportsPermissionSchedules = action.payload.supportsPermissionSchedules !== false;
			state.schedules.status = S.SUCCEEDED;
		},
		schedulesFailed: (state, action) => {
			state.schedules.status = S.FAILED;
			state.schedules.error = action.payload;
		},
		// payload: { accessWindowID, add: [{ deviceID, action }], remove: [deviceID] }
		scheduleDevicesSaveRequested: (state, action) => {
			state.deviceSaves[action.payload.accessWindowID] = { running: true, rows: [] };
		},
		scheduleDevicesSaveRowsReceived: (state, action) => {
			const { accessWindowID, rows } = action.payload;
			const save = state.deviceSaves[accessWindowID] ?? { running: true, rows: [] };
			const byID = new Map(save.rows.map((r) => [r.deviceID, r]));
			rows.forEach((r) => byID.set(r.deviceID, r));
			save.rows = [...byID.values()];
			state.deviceSaves[accessWindowID] = save;
		},
		scheduleDevicesSaveFinished: (state, action) => {
			const save = state.deviceSaves[action.payload.accessWindowID];
			if (save) save.running = false;
		},
		scheduleDevicesSaveDismissed: (state, action) => {
			delete state.deviceSaves[action.payload.accessWindowID];
		},

		// payload: { type: EDITOR_TYPES, accessWindowID?, targetID? }
		editorOpened: (state, action) => {
			state.editor = { accessWindowID: null, targetID: null, ...action.payload };
		},
		editorClosed: (state) => {
			state.editor = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
		builder.addCase('auth/switchProfileSuccess', () => initialState);
	}
});

export const openAccessActions = openAccessSlice.actions;
export default openAccessSlice.reducer;
